import { useMutation, useQueryClient } from '@tanstack/react-query';
import { reservationQueue, parseApiError } from '@ooh/contracts';
import type { JoinQueueBody } from '@ooh/contracts';
import { toast } from '@ooh/ui/components/sonner';
import { invalidateQueueCaches } from './mutation-helpers';
import { useApiClient } from '../../plugs/api-client';
import { apiErrorToUserMessage } from '../../lib/api-error-messages';

// ─── Join waitlist ────────────────────────────────────────────

interface JoinWaitlistVars extends JoinQueueBody {
  mediaUnitId: number;
}

export function useJoinWaitlist() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: JoinWaitlistVars) =>
      api.call(reservationQueue.join, {
        mediaUnitId: vars.mediaUnitId,
        offerId: vars.offerId,
        // CU-86cb7ewja: clientId/brand were previously dropped here, so every
        // no-offer join request reached the server with no client identity.
        clientId: vars.clientId,
        brand: vars.brand,
        subtype: vars.subtype,
        dateFrom: vars.dateFrom,
        dateTo: vars.dateTo,
        // REQUIRED shared "do kiedy" deadline (CU-86cbc4t4q) — see JoinQueueBodySchema.
        ttlExpiresAt: vars.ttlExpiresAt,
      }),
    onSuccess: (_data, { mediaUnitId }) => invalidateQueueCaches(queryClient, mediaUnitId),
    onError: (error: unknown) =>
      toast.error(apiErrorToUserMessage(parseApiError(error), 'mutation')),
  });
}

// ─── Leave / admin-remove waitlist entry ──────────────────────

interface LeaveWaitlistVars {
  id: number;
  mediaUnitId?: number;
}

export function useLeaveWaitlist() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, LeaveWaitlistVars>({
    mutationFn: ({ id }) => api.call(reservationQueue.leave, { id }),
    onSuccess: (_data, { mediaUnitId }) => invalidateQueueCaches(queryClient, mediaUnitId),
    onError: (error: unknown) =>
      toast.error(apiErrorToUserMessage(parseApiError(error), 'mutation')),
  });
}

// ─── Admin displace-and-promote ───────────────────────────────

interface AdminDisplaceAndPromoteVars {
  id: number;
  mediaUnitId?: number;
}

export function useAdminDisplaceAndPromote() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, AdminDisplaceAndPromoteVars>({
    mutationFn: ({ id }) => api.call(reservationQueue.adminPromote, { id }),
    onSuccess: (_data, { mediaUnitId }) => invalidateQueueCaches(queryClient, mediaUnitId),
    onError: (error: unknown) =>
      toast.error(apiErrorToUserMessage(parseApiError(error), 'mutation')),
  });
}
